import { useTranslation } from "react-i18next";
import { ProgressBar } from "@/shared/ui/ProgressBar";
import type { AgreementPaymentSlot } from "../types";

// Shared payment-progress block: the segment bar + colour legend + summary tiles. Used by BOTH the
// post-add edit drawer (real schedule from the detail endpoint) and the pre-commit preview drawer
// (the PROJECTED schedule the backend returns on dry-run — drift-locked to the post-commit schedule).
// Renders nothing when there is no schedule. `paidAmount`/`remainder` accept number|string (the
// detail endpoint sends numbers, the preview row sends money strings); both are coerced for display.
export function AgreementProgressSection({
  payments,
  paidAmount,
  remainder,
  startDate,
}: {
  payments: AgreementPaymentSlot[];
  paidAmount: number | string;
  remainder: number | string;
  startDate?: string | null;
}) {
  const { t } = useTranslation();
  if (payments.length === 0) return null;
  // "ne zaman başladı" — schedule start; fall back to the first slot's date.
  const started = startDate ?? payments[0]?.payment_date ?? "—";
  // "hangi ayda kaldı" — the first not-yet-paid installment (current month); all paid → finished.
  const currentSlot = payments.find((p) => p.status !== "done");
  const current = currentSlot
    ? `${currentSlot.number ?? "—"} / ${payments.length}`
    : t("import.agreements.progress.currentDone");
  const finish = payments[payments.length - 1].payment_date ?? "—";
  return (
    <div className="mb-5">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-grey-400">
        {t("import.agreements.progress.title")}
      </p>
      <ProgressBar slots={payments} />
      {/* Legend for the three segment colours. */}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-grey-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-green" />
          {t("import.agreements.progress.legendDone")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" />
          {t("import.agreements.progress.legendPartial")}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-grey-200" />
          {t("import.agreements.progress.legendUnpaid")}
        </span>
      </div>
      {/* Summary tiles: started, current installment, paid so far, installments left, finish, remaining debt. */}
      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-[13px] sm:grid-cols-3">
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.started")}</span>
          <span className="font-medium text-grey-700">{started}</span>
        </div>
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.current")}</span>
          <span className="font-medium text-grey-700">{current}</span>
        </div>
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.paid")}</span>
          <span className="font-medium text-grey-700 tnum">{Number(paidAmount).toLocaleString()}</span>
        </div>
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.left")}</span>
          <span className="font-medium text-grey-700 tnum">
            {payments.filter((p) => p.status !== "done").length}
          </span>
        </div>
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.finish")}</span>
          <span className="font-medium text-grey-700">{finish}</span>
        </div>
        <div>
          <span className="block text-grey-500">{t("import.agreements.progress.remaining")}</span>
          <span className="font-medium text-grey-700 tnum">{Number(remainder).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}
